import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Plus, Trash2 } from "lucide-react";
import { adminCreateProduct, adminUpdateProduct, adminListSizes, adminListPapers, adminListFinishes } from "../../lib/admin";
import PricingTableEditor from "./PricingTableEditor";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const emptyProduct = {
  name: "",
  category: "general",
  description: "",
  pricing_notes: "",
  is_active: true,
  allowed_size_ids: [],
  default_size_id: null,
  custom_sizes: [],
  allowed_paper_ids: [],
  default_paper_id: null,
  custom_papers: [],
  allowed_finish_ids: [],
  default_finish_ids: [],
  required_finish_ids: [],
  exclusive_finish_groups: [],
  size_multipliers: {},
  quantity_tiers: [
    { min: 1, max: 99, discount_pct: 0 },
    { min: 100, max: 249, discount_pct: 10 },
    { min: 250, max: 499, discount_pct: 20 },
    { min: 500, max: 999, discount_pct: 30 },
    { min: 1000, max: 999999, discount_pct: 40 },
  ],
  min_quantity: 1,
  quantity_multiple: 1,
  has_pages: false,
  page_price_per_sheet: 0,
  page_multiple: 1,
  min_pages: 1,
  max_pages: null,
  setup_fee: 0,
  minimum_charge: 0,
  price_strategy: "formula",
  price_table: {},
};

const Section = ({ title, children, hint }) => (
  <div className="border-t pt-5" style={{ borderColor: "var(--ups-border)" }}>
    <div className="overline mb-3">{title}</div>
    {hint && <p className="text-[11px] text-[color:var(--ups-subtext)] mb-3">{hint}</p>}
    {children}
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="text-xs font-mono px-2.5 py-1.5 rounded-sm transition"
    style={{
      background: active ? "var(--ups-ink)" : "#fff",
      color: active ? "#fff" : "var(--ups-ink)",
      border: `1px solid ${active ? "var(--ups-ink)" : "var(--ups-border)"}`,
    }}
  >
    {children}
  </button>
);

const ProductEditor = ({ product, onClose, onSaved }) => {
  const [p, setP] = useState(() => ({ ...emptyProduct, ...(product || {}) }));
  const [sizes, setSizes] = useState([]);
  const [papers, setPapers] = useState([]);
  const [finishes, setFinishes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [libLoading, setLibLoading] = useState(true);
  const isEdit = Boolean(product?.id);

  useEffect(() => {
    (async () => {
      try {
        const [s, pp, f] = await Promise.all([adminListSizes(), adminListPapers(), adminListFinishes()]);
        setSizes(s);
        setPapers(pp);
        setFinishes(f);
      } catch {
        toast.error("Failed to load libraries");
      } finally {
        setLibLoading(false);
      }
    })();
  }, []);

  const toggle = (key, id) => {
    const list = p[key] || [];
    if (list.includes(id)) {
      setP({ ...p, [key]: list.filter((x) => x !== id) });
    } else {
      setP({ ...p, [key]: [...list, id] });
    }
  };

  const save = async () => {
    if (!p.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!p.default_size_id && (p.allowed_size_ids.length > 0 || (p.custom_sizes || []).length > 0)) {
      // pick first as default
      p.default_size_id = p.allowed_size_ids[0] || p.custom_sizes?.[0]?.id || null;
    }
    setSaving(true);
    try {
      const payload = {
        name: p.name,
        category: p.category || "general",
        description: p.description || "",
        pricing_notes: p.pricing_notes || "",
        is_active: Boolean(p.is_active),
        allowed_size_ids: p.allowed_size_ids || [],
        default_size_id: p.default_size_id || null,
        custom_sizes: p.custom_sizes || [],
        allowed_paper_ids: p.allowed_paper_ids || [],
        default_paper_id: p.default_paper_id || null,
        custom_papers: p.custom_papers || [],
        allowed_finish_ids: p.allowed_finish_ids || [],
        default_finish_ids: p.default_finish_ids || [],
        required_finish_ids: p.required_finish_ids || [],
        exclusive_finish_groups: p.exclusive_finish_groups || [],
        size_multipliers: p.size_multipliers || {},
        quantity_tiers: p.quantity_tiers || [],
        min_quantity: Number(p.min_quantity) || 1,
        quantity_multiple: Number(p.quantity_multiple) || 1,
        has_pages: Boolean(p.has_pages),
        page_price_per_sheet: Number(p.page_price_per_sheet) || 0,
        page_multiple: Number(p.page_multiple) || 1,
        min_pages: Number(p.min_pages) || 1,
        max_pages: p.max_pages ? Number(p.max_pages) : null,
        setup_fee: Number(p.setup_fee) || 0,
        minimum_charge: Number(p.minimum_charge) || 0,
        price_strategy: p.price_strategy || "formula",
        price_table: p.price_table || {},
      };
      if (isEdit) {
        await adminUpdateProduct(product.id, payload);
        toast.success("Product updated");
      } else {
        await adminCreateProduct(payload);
        toast.success("Product created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addCustomSize = () => {
    const id = window.prompt("Size id (e.g. 4x9):");
    if (!id) return;
    const label = window.prompt("Label (e.g. 4 × 9 in):") || id;
    const w = parseFloat(window.prompt("Width in inches:") || "0");
    const h = parseFloat(window.prompt("Height in inches:") || "0");
    const mult = parseFloat(window.prompt("Size multiplier (relative to 8.5x11 = 1.0):") || "1");
    setP({
      ...p,
      custom_sizes: [...(p.custom_sizes || []), { id, label, width_in: w, height_in: h, multiplier: mult }],
    });
  };

  const removeCustomSize = (id) => {
    setP({ ...p, custom_sizes: (p.custom_sizes || []).filter((s) => s.id !== id) });
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="product-editor-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative ml-auto w-full sm:max-w-2xl bg-[color:var(--ups-paper)] h-full overflow-y-auto border-l-2 border-[color:var(--ups-ink)]">
        <div className="sticky top-0 z-10 bg-[color:var(--ups-ink)] text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="overline" style={{ color: "#FFB500" }}>
              {isEdit ? "Edit Product" : "New Product"}
            </div>
            <h3 className="font-display text-lg font-bold">{p.name || "Untitled"}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-sm">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {libLoading && (
            <div className="flex items-center gap-2 text-sm text-[color:var(--ups-subtext)]">
              <Loader2 size={14} className="animate-spin" /> Loading options…
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Name</label>
              <input
                className="input-swiss w-full px-3 py-2 rounded-sm"
                value={p.name}
                onChange={(e) => setP({ ...p, name: e.target.value })}
                data-testid="product-name-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Category</label>
              <input
                className="input-swiss w-full px-3 py-2 rounded-sm"
                value={p.category}
                onChange={(e) => setP({ ...p, category: e.target.value })}
                data-testid="product-category-input"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!p.is_active}
                  onChange={(e) => setP({ ...p, is_active: e.target.checked })}
                  data-testid="product-active-toggle"
                />
                Active (shown to the bot)
              </label>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Description</label>
              <textarea
                rows={2}
                className="input-swiss w-full px-3 py-2 rounded-sm"
                value={p.description}
                onChange={(e) => setP({ ...p, description: e.target.value })}
                data-testid="product-description-input"
              />
            </div>
          </div>

          <Section title="Sizes" hint="Pick from the global library. Add custom sizes if needed (e.g. 3.5×2 for business cards).">
            <div className="flex flex-wrap gap-2 mb-3">
              {sizes.map((s) => (
                <Chip
                  key={s.id}
                  active={(p.allowed_size_ids || []).includes(s.id)}
                  onClick={() => toggle("allowed_size_ids", s.id)}
                  testid={`size-chip-${s.id}`}
                >
                  {s.label} ({s.multiplier}×)
                </Chip>
              ))}
            </div>
            {(p.custom_sizes || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {(p.custom_sizes || []).map((s) => (
                  <span key={s.id} className="text-xs font-mono px-2.5 py-1.5 flex items-center gap-2"
                    style={{ background: "var(--ups-gold)", color: "var(--ups-ink)", border: "1px solid var(--ups-ink)" }}>
                    {s.label} ({s.multiplier}×)
                    <button onClick={() => removeCustomSize(s.id)} aria-label="remove">
                      <Trash2 size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3">
              <button type="button" onClick={addCustomSize} className="text-xs font-bold uppercase tracking-[0.12em] flex items-center gap-1 px-2 py-1 hover:bg-[color:var(--ups-muted)] rounded-sm" data-testid="add-custom-size">
                <Plus size={12} /> Custom size
              </button>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-bold uppercase tracking-[0.12em]">Default:</span>
                <select
                  className="input-swiss px-2 py-1 text-xs rounded-sm"
                  value={p.default_size_id || ""}
                  onChange={(e) => setP({ ...p, default_size_id: e.target.value || null })}
                  data-testid="product-default-size"
                >
                  <option value="">—</option>
                  {[...(p.allowed_size_ids || []), ...((p.custom_sizes || []).map((s) => s.id))].map((sid) => (
                    <option key={sid} value={sid}>{sid}</option>
                  ))}
                </select>
              </div>
            </div>
          </Section>

          <Section title="Papers" hint="Stock options available for this product.">
            <div className="flex flex-wrap gap-2 mb-3">
              {papers.map((pp) => (
                <Chip
                  key={pp.id}
                  active={(p.allowed_paper_ids || []).includes(pp.id)}
                  onClick={() => toggle("allowed_paper_ids", pp.id)}
                  testid={`paper-chip-${pp.id}`}
                >
                  {pp.label} · {fmtUSD(pp.unit_price)}
                </Chip>
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-bold uppercase tracking-[0.12em]">Default:</span>
              <select
                className="input-swiss px-2 py-1 text-xs rounded-sm"
                value={p.default_paper_id || ""}
                onChange={(e) => setP({ ...p, default_paper_id: e.target.value || null })}
                data-testid="product-default-paper"
              >
                <option value="">—</option>
                {(p.allowed_paper_ids || []).map((pid) => (
                  <option key={pid} value={pid}>{pid}</option>
                ))}
              </select>
            </div>
          </Section>

          <Section
            title="Finishing"
            hint="Allowed = the customer can opt in. Required = always applied (e.g. brochures are always double sided). Exclusive groups below ensure one choice per group (e.g. half-fold OR tri-fold)."
          >
            <div className="text-[11px] font-bold uppercase tracking-[0.12em] mb-1">Allowed</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {finishes.map((f) => (
                <Chip
                  key={f.id}
                  active={(p.allowed_finish_ids || []).includes(f.id)}
                  onClick={() => toggle("allowed_finish_ids", f.id)}
                  testid={`finish-allowed-${f.id}`}
                >
                  {f.label}
                </Chip>
              ))}
            </div>
            <div className="text-[11px] font-bold uppercase tracking-[0.12em] mb-1">Required (auto-applied)</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {finishes.map((f) => (
                <Chip
                  key={f.id}
                  active={(p.required_finish_ids || []).includes(f.id)}
                  onClick={() => toggle("required_finish_ids", f.id)}
                  testid={`finish-required-${f.id}`}
                >
                  {f.label}
                </Chip>
              ))}
            </div>
            <div className="text-[11px] font-bold uppercase tracking-[0.12em] mb-1">
              Exclusive groups (JSON: array of arrays)
            </div>
            <textarea
              rows={2}
              className="input-swiss w-full px-3 py-2 rounded-sm font-mono text-xs"
              placeholder='e.g. [["half_fold","tri_fold"]]'
              value={JSON.stringify(p.exclusive_finish_groups || [])}
              onChange={(e) => {
                try {
                  setP({ ...p, exclusive_finish_groups: JSON.parse(e.target.value) });
                } catch {
                  // ignore parse errors until blur
                  setP({ ...p, exclusive_finish_groups_raw: e.target.value });
                }
              }}
              data-testid="exclusive-groups-input"
            />
          </Section>

          <Section title="Quantity">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Min quantity</label>
                <input
                  type="number"
                  className="input-swiss w-full px-3 py-2 rounded-sm font-mono"
                  value={p.min_quantity}
                  onChange={(e) => setP({ ...p, min_quantity: e.target.value })}
                  data-testid="product-min-qty"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Multiple of</label>
                <input
                  type="number"
                  className="input-swiss w-full px-3 py-2 rounded-sm font-mono"
                  value={p.quantity_multiple}
                  onChange={(e) => setP({ ...p, quantity_multiple: e.target.value })}
                  data-testid="product-qty-multiple"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">
                Volume tiers (JSON)
              </label>
              <textarea
                rows={3}
                className="input-swiss w-full px-3 py-2 rounded-sm font-mono text-xs"
                value={JSON.stringify(p.quantity_tiers || [], null, 2)}
                onChange={(e) => {
                  try {
                    setP({ ...p, quantity_tiers: JSON.parse(e.target.value) });
                  } catch {
                    /* ignore */
                  }
                }}
                data-testid="product-qty-tiers"
              />
            </div>
          </Section>

          <Section title="Pages" hint="For multi-page products like Booklets, Catalogs, Manuals, Notepads.">
            <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={!!p.has_pages}
                onChange={(e) => setP({ ...p, has_pages: e.target.checked })}
                data-testid="product-has-pages"
              />
              This product has pages (per-page pricing applies)
            </label>
            {p.has_pages && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">$ per page</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-swiss w-full px-3 py-2 rounded-sm font-mono"
                    value={p.page_price_per_sheet}
                    onChange={(e) => setP({ ...p, page_price_per_sheet: e.target.value })}
                    data-testid="product-page-price"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Page multiple</label>
                  <input
                    type="number"
                    className="input-swiss w-full px-3 py-2 rounded-sm font-mono"
                    value={p.page_multiple}
                    onChange={(e) => setP({ ...p, page_multiple: e.target.value })}
                    data-testid="product-page-multiple"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Min pages</label>
                  <input
                    type="number"
                    className="input-swiss w-full px-3 py-2 rounded-sm font-mono"
                    value={p.min_pages}
                    onChange={(e) => setP({ ...p, min_pages: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Max pages</label>
                  <input
                    type="number"
                    className="input-swiss w-full px-3 py-2 rounded-sm font-mono"
                    value={p.max_pages || ""}
                    onChange={(e) => setP({ ...p, max_pages: e.target.value })}
                  />
                </div>
              </div>
            )}
          </Section>

          <Section title="Charges">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Setup fee</label>
                <input
                  type="number"
                  step="0.01"
                  className="input-swiss w-full px-3 py-2 rounded-sm font-mono"
                  value={p.setup_fee}
                  onChange={(e) => setP({ ...p, setup_fee: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-2">Minimum charge</label>
                <input
                  type="number"
                  step="0.01"
                  className="input-swiss w-full px-3 py-2 rounded-sm font-mono"
                  value={p.minimum_charge}
                  onChange={(e) => setP({ ...p, minimum_charge: e.target.value })}
                />
              </div>
            </div>
          </Section>

          <Section title="Pricing notes" hint="Free-form notes fed to the LLM. Good place for nuances not captured by fields.">
            <textarea
              rows={3}
              className="input-swiss w-full px-3 py-2 rounded-sm text-sm"
              value={p.pricing_notes}
              onChange={(e) => setP({ ...p, pricing_notes: e.target.value })}
              data-testid="product-notes-input"
            />
          </Section>

          <Section
            title={`Pricing Table (${p.price_strategy || "formula"})`}
            hint="Edit retail prices directly. UPS Store prices auto-compute at 40% off. Changes save with this product."
          >
            <div className="mb-3 flex items-center gap-3">
              <label className="block text-xs font-bold uppercase tracking-[0.18em]">Strategy</label>
              <select
                className="input-swiss px-3 py-2 text-sm rounded-sm"
                value={p.price_strategy || "formula"}
                onChange={(e) => setP({ ...p, price_strategy: e.target.value })}
                data-testid="product-price-strategy"
              >
                <option value="formula">formula (paper × size × qty)</option>
                <option value="tiered">tiered (per-unit lookup table)</option>
                <option value="booklet">booklet (base + per-extra-4-page)</option>
                <option value="tiered_total">tiered_total (total order price)</option>
              </select>
            </div>
            <PricingTableEditor
              product={p}
              onChange={(pt) => setP({ ...p, price_table: pt })}
            />
          </Section>

          <button
            onClick={save}
            disabled={saving}
            className="btn-brutal w-full py-3 px-4 rounded-sm flex items-center justify-center gap-2"
            data-testid="product-save-button"
          >
            {saving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <>Save Product</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductEditor;
